import { PRadioGroup, PRadioGroupOption, PText } from "@porsche-design-system/components-react"
import type { RadioGroupChangeEventDetail } from "@porsche-design-system/components-react"
import type { CustomerAddressView } from "@slfinrtl/ui-bff-routes"
import { useState } from "react"
import type { IntlShape } from "react-intl"
import { useIntl } from "react-intl"
import { customerAddressListMessages } from "./customerAddressList.messages"

type FormatMessage = IntlShape["formatMessage"]

const customerAddressListTestId = "customer-address-list"

export interface CustomerAddressListProps {
	addresses: CustomerAddressView[]
	disabled?: boolean
	/** called every time the user selects another address */
	onChange: (address: CustomerAddressView) => void
}

export const CustomerAddressList = ({ addresses, disabled, onChange }: CustomerAddressListProps) => {
	const { formatMessage } = useIntl()
	const [selectedValue, setSelectedValue] = useState(() => {
		const selectedIndex = addresses.findIndex((address) => address.selected)
		return selectedIndex >= 0 ? selectedIndex.toString() : undefined
	})

	const handleChange = (event: CustomEvent<RadioGroupChangeEventDetail>) => {
		const value = (event.target as HTMLInputElement).value
		setSelectedValue(value)
		onChange(addresses[Number(value)])
	}

	return (
		<PRadioGroup
			data-testid={customerAddressListTestId}
			name="customerAddressList"
			value={selectedValue}
			disabled={disabled}
			onChange={handleChange}
		>
			{addresses.map((address, index) => (
				<PRadioGroupOption key={index} value={index.toString()}>
					<span slot="label">
						<PText weight="bold" color={disabled ? "contrast-medium" : undefined}>
							{getAddressTitle(address, formatMessage)}
						</PText>
						{address.formatted.map((line, lineIndex) => (
							<PText key={lineIndex} color={disabled ? "contrast-medium" : undefined}>
								{line}
							</PText>
						))}
					</span>
				</PRadioGroupOption>
			))}
		</PRadioGroup>
	)
}

const getAddressTitle = (address: CustomerAddressView, formatMessage: FormatMessage): string => {
	const title = formatMessage(
		address.type === "HOME" ? customerAddressListMessages.homeAddress : customerAddressListMessages.workAddress,
	)

	const intendedUses = [
		...(address.isCommunication ? [formatMessage(customerAddressListMessages.communicationAddress)] : []),
		...(address.isInvoice ? [formatMessage(customerAddressListMessages.invoiceAddress)] : []),
	]

	return intendedUses.length > 0 ? `${title} (${intendedUses.join(", ")})` : title
}
